import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY = { slug: "", title: "", category: "Basics", content: "", published: true };

export default function DocsAdmin() {
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | doc
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/docs").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/docs", form);
        toast.success("Page created");
      } else {
        await api.patch(`/admin/docs/${editing.id}`, form);
        toast.success("Page saved");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const togglePublish = async (d) => {
    await api.patch(`/admin/docs/${d.id}`, { published: !d.published });
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    await api.delete(`/admin/docs/${d.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-docs">
      <PageTitle title="Documentation Management" sub={`${docs.length} pages · publish, edit, organise.`}>
        <button onClick={() => { setEditing("new"); setForm(EMPTY); }} data-testid="doc-create-open"
          className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-150">
          <Plus size={15} /> New page
        </button>
      </PageTitle>

      <div className="space-y-2" data-testid="docs-list">
        {docs.map((d) => (
          <div key={d.id} className="rounded-lg border border-white/5 bg-panel px-5 py-4 flex items-center justify-between gap-4" data-testid={`doc-row-${d.slug}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <p className="font-heading font-bold text-white text-sm">{d.title}</p>
                <Badge tone="grey">{d.category}</Badge>
                <Badge tone={d.published ? "green" : "amber"}>{d.published ? "published" : "draft"}</Badge>
              </div>
              <p className="font-mono text-xs text-white/30 mt-1">/docs/{d.slug}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => togglePublish(d)} className="text-xs font-semibold text-volt border border-volt/30 rounded-full px-3 py-1.5 hover:bg-volt/10 transition-colors duration-150" data-testid={`doc-publish-${d.slug}`}>
                {d.published ? "Unpublish" : "Publish"}
              </button>
              <button onClick={() => { setEditing(d); setForm({ slug: d.slug, title: d.title, category: d.category, content: d.content, published: d.published }); }} className="p-2 text-white/40 hover:text-white transition-colors duration-150" data-testid={`doc-edit-${d.slug}`} aria-label="Edit"><Pencil size={14} /></button>
              <button onClick={() => remove(d)} className="p-2 text-white/40 hover:text-red-400 transition-colors duration-150" data-testid={`doc-delete-${d.slug}`} aria-label="Delete"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-panel border-white/10 text-white max-w-2xl" data-testid="doc-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing === "new" ? "New documentation page" : `Edit ${form.title}`}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, ...(editing === "new" ? { slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") } : {}) })} required placeholder="Title"
                className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple" data-testid="doc-title-input" />
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required placeholder="slug" disabled={editing !== "new"}
                className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blurple disabled:opacity-50" data-testid="doc-slug-input" />
            </div>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} required placeholder="Category (Basics, Playback, Help…)"
              className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blurple" data-testid="doc-category-input" />
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={12} placeholder={"## Heading\nParagraph with `code` and **bold**."}
              className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blurple resize-y scanlines" data-testid="doc-content-input" />
            <button type="submit" className="w-full bg-blurple hover:bg-[#4752c4] text-white font-semibold py-3 rounded-full transition-colors duration-150" data-testid="doc-save">Save page</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
